// UploadDocumentModal.jsx — 2026-08-06 attribute-first ingestion.
//
// Reusable modal that lets an admin drop a document (PDF / txt /
// image) directly into the ingestion pipeline from any folder page
// or the Atlas dashboard. Search-picker resolves ambiguous names by
// full breadcrumb ("fleet royal" → the Fleet folder under Royal
// Caribbean, not Carnival's fleet).
//
// Wire: <UploadDocumentModal open onClose currentSlug currentName />

import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function UploadDocumentModal({ open, onClose,
                                               currentSlug = "",
                                               currentName = "" }) {
  const [file, setFile] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [pickedSlug, setPickedSlug] = useState(currentSlug || "");
  const [pickedName, setPickedName] = useState(currentName || "");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPickedSlug(currentSlug || "");
    setPickedName(currentName || "");
    setFile(null);
    setQ("");
    setHits([]);
  }, [open, currentSlug, currentName]);

  useEffect(() => {
    if (!q || q.length < 2) { setHits([]); return; }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/atlas/topics/search`, {
          params: { q, limit: 8 } });
        if (!cancelled) setHits(data?.topics || []);
      } catch { if (!cancelled) setHits([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [q]);

  const doUpload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (pickedSlug) fd.append("target_slug", pickedSlug);
      // Axios instance from lib/api.js sets a default
      // `Content-Type: application/json` on every request. For a
      // multipart FormData upload we MUST override it to `undefined`
      // — that tells axios to compute the correct
      // `multipart/form-data; boundary=…` header itself. Without
      // this override the server sees a JSON content-type over a
      // multipart body and returns 422 (Bug 1).
      const { data } = await api.post("/atlas/ingest/upload", fd, {
        headers: { "Content-Type": undefined },
        transformRequest: [(d) => d], // keep FormData intact
      });
      const upId = data?.upload_id;
      const factCount = data?.fact_count || 0;
      toast.success(
        `Upload started · ${factCount} candidate${factCount===1?"":"s"} extracted`
        + (pickedName ? ` · scoped to ${pickedName}` : ""));
      onClose?.();
      if (upId) {
        // Take the user to the review panel — auto-applied rows will
        // already be marked "approved" there.
        setTimeout(() => {
          window.location.href = `/atlas/admin/ingest/${upId}`;
        }, 300);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-tb-navy/40 flex items-center justify-center p-4"
         data-testid="atlas-upload-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-tb-navy text-lg"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              Upload document
            </h3>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm">
              Attributes and narratives will be extracted automatically.
              High-confidence facts auto-apply; ambiguous ones go to review.
            </p>
          </div>
          <button type="button" onClick={onClose}
            aria-label="Close" data-testid="atlas-upload-close"
            className="text-tb-navy/60 hover:text-tb-navy">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
            Folder (where these facts belong)
          </span>
          {pickedSlug && (
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-2 py-1.5"
                 data-testid="atlas-upload-selected-folder">
              <span className="font-hand text-sm text-emerald-900 truncate flex-1">
                📁 {pickedName || pickedSlug}
              </span>
              <button type="button" onClick={() => { setPickedSlug(""); setPickedName(""); }}
                className="text-tb-navy/50 hover:text-tb-navy text-xs">
                change
              </button>
            </div>
          )}
          {!pickedSlug && (
            <div className="relative mt-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tb-navy/40" />
              <input type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search folders (e.g. fleet royal, wonderland)"
                data-testid="atlas-upload-folder-search"
                className="w-full h-9 pl-7 pr-2 rounded-lg border border-tb-navy/25 font-hand text-sm text-tb-navy" />
              {hits.length > 0 && (
                <div className="mt-1 rounded-lg border border-tb-navy/15 bg-white shadow max-h-56 overflow-y-auto"
                     data-testid="atlas-upload-folder-hits">
                  {hits.map((h) => (
                    <button key={h.slug} type="button"
                      onClick={() => { setPickedSlug(h.slug); setPickedName(h.name); setQ(""); setHits([]); }}
                      data-testid={`atlas-upload-folder-hit-${h.slug}`}
                      className="w-full text-left px-2.5 py-1.5 hover:bg-tb-navy/5 font-hand text-sm text-tb-navy">
                      <div>{h.name}</div>
                      {h.breadcrumb && (
                        <div className="text-[10px] text-tb-navy/50">{h.breadcrumb}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </label>

        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/40">
            Document
          </span>
          <input type="file"
            accept=".pdf,.txt,.md,.csv,.jpg,.jpeg,.png,.webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="atlas-upload-file"
            className="mt-1 w-full font-hand text-sm text-tb-navy" />
          {file && (
            <div className="mt-1 text-[11px] font-hand text-tb-navy/60">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </label>

        <div className="mt-5 flex items-center justify-end gap-2">
          {uploading && (
            <span className="mr-auto font-hand text-[11px] text-tb-navy/55"
                  data-testid="atlas-upload-progress-hint">
              Extracting facts — this can take 10–15s.
            </span>
          )}
          <button type="button" onClick={onClose} disabled={uploading}
            data-testid="atlas-upload-cancel"
            className="h-9 px-4 rounded-full text-tb-navy/60 hover:text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button"
            onClick={doUpload}
            disabled={!file || uploading}
            data-testid="atlas-upload-submit"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm hover:bg-tb-navy/90 disabled:opacity-40">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
